import React from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { isNil, isEqual } from 'lodash';
import { openModal } from 'actions/modalActions';
import { Grid } from 'components/shared/Grid';
import { EquationCellRenderer } from 'components/shared/EquationCellRenderer';
import {
  ADD_KPI_GROUP,
  REMOVE_KPI_GROUP,
  EDIT_KPI_GROUP,
  MOVE_KPI_GROUP_UP,
  MOVE_KPI_GROUP_DOWN,
  MOVE_KPI_UP,
  MOVE_KPI_DOWN,
  KPI_REMOVE,
  SET_COLUMN_STATE,
  SET_GROUP_STATE,
} from 'constants/actionTypes';
import { kpiRowsSelector, kpiRowsDetailSelector } from 'selectors/kpis';
import {
  periodColumnsForKpiGridSelector,
  periodIdsSelector,
} from 'selectors/periods';
import { gridStateSelector } from 'selectors/gridState';
import { settingsSelector } from 'selectors/settings';
import {
  gridComponentDidUpdate,
  handleGridChange,
  handleGridReady,
} from 'utils/grid';
import { rowToKpiGroup } from 'utils/gridToState';
import { copyCellKpiFormatter } from 'utils/generic';
import { KPIS_PATH } from 'constants/paths';
import { KPIS_GRID } from 'constants/grid';
import { ContentType } from 'constants/types';
import { KpiCalculationResultType } from 'constants/kpis';

class KpiGrid extends React.PureComponent {
  constructor(props) {
    super(props);

    this.frameworkComponents = {
      equationCellRenderer: (params) => <EquationCellRenderer {...params} />,
    };
  }

  state = {
    detail: false,
  };

  defaultColDef = {
    sortable: false,
  };

  autoGroupColumnDef = {
    headerName: 'Groups',
    field: 'name',
    colId: 'name',
    width: 300,
    editable: (params) => params.data.group,
    cellRendererParams: {
      suppressCount: true,
      suppressDoubleClickExpand: true,
    },
  };

  getCols = (isDetail = false) => {
    const { cols } = this.props;

    if (isDetail) {
      const detailCols = [
        {
          field: 'parameter',
          colId: 'parameter',
          headerName: 'Parameter',
        },
      ];

      return [...detailCols, ...cols];
    }

    return cols;
  };

  componentDidUpdate(prevProps) {
    const {
      periodIds,
      colState,
      setColumnState,
      setGroupState,
      path,
    } = this.props;
    const havePeriodsChanged = !isEqual(prevProps.periodIds, periodIds);
    if (havePeriodsChanged) {
      gridComponentDidUpdate(
        colState,
        setColumnState,
        setGroupState,
        path,
        KPIS_GRID,
        this.columnApi
      );
    }
  }

  handleCellValueChanged = (event) => {
    const { editKpiGroup } = this.props;
    editKpiGroup(rowToKpiGroup(event.data));
  };

  handleGridReady = ({ api, columnApi }) => {
    const { colState, path } = this.props;

    handleGridReady(path, colState, KPIS_GRID, api, columnApi);

    this.columnApi = columnApi;
    this.gridApi = api;
  };

  handleColumnResized = ({ columnApi, finished, source }) => {
    if (source === 'uiColumnDragged' && finished) {
      const { colState, setColumnState, setGroupState, path } = this.props;
      handleGridChange(
        path,
        colState,
        setColumnState,
        setGroupState,
        KPIS_GRID,
        columnApi
      );
    }
  };

  handleColumnVisible = ({ columnApi }) => {
    const { colState, setColumnState, setGroupState, path } = this.props;
    handleGridChange(
      path,
      colState,
      setColumnState,
      setGroupState,
      KPIS_GRID,
      columnApi
    );
  };

  shouldSuppressKeyboardEvent = (event) => {
    const ctrlKeysToSuppress = ['ArrowUp', 'ArrowDown'];

    if (event.ctrlKey) {
      return ctrlKeysToSuppress.includes(event.key);
    }
  };

  onCellKeyDown = (e) => {
    if (!e.node || !e.node.data) {
      return;
    }
    const { event, node, rowIndex } = e;

    const contentType = node && node.data.contentType;
    const moveUp = {
      [ContentType.kpiGroup]: this.props.moveKpiGroupUp,
      [ContentType.kpi]: this.props.moveKpiUp,
    };
    const moveDown = {
      [ContentType.kpiGroup]: this.props.moveKpiGroupDown,
      [ContentType.kpi]: this.props.moveKpiDown,
    };

    const ctrlKeysActions = {
      ArrowUp: () => moveUp[contentType](node ? node.data.id : 0),
      ArrowDown: () => moveDown[contentType](node ? node.data.id : 0),
    };

    if (event.ctrlKey && event.key in ctrlKeysActions) {
      e.api.setFocusedCell(rowIndex, 'name', null);
      ctrlKeysActions[event.key]();
    }
  };

  openKpiEntryModal = (kpiEntryId) => {
    this.props.openModal({
      id: uuid(),
      type: 'component',
      data: { kpiEntryId },
      componentType: 'KpiEntryModal',
    });
  };

  onCellDoubleClicked = ({ data }) => {
    const { id } = data;
    const isKpi = data.contentType === ContentType.kpi;
    if (isKpi) {
      this.openKpiEntryModal(id);
    }
  };

  getRowHeight = (params) => {
    return 40;
  };

  processCellForClipboard = (params) => {
    const { settings } = this.props;

    const cellFormatter = copyCellKpiFormatter(settings);

    return cellFormatter(params);
  };

  getGroupContextMenuItems = (params) => {
    return [
      {
        name: 'Group options',
        disabled: !params.node,
        subMenu: [
          {
            name: 'Move Up',
            shortcut: 'Ctrl + Up',
            action: () => this.props.moveKpiGroupUp(params.node.data.id),
          },
          {
            name: 'Move Down',
            shortcut: 'Ctrl + Down',
            action: () => this.props.moveKpiGroupDown(params.node.data.id),
          },
        ],
      },
      'separator',
      {
        name: `New KPI`,
        disabled: !params.node,
        action: () =>
          this.props.openModal({
            id: uuid(),
            type: 'component',
            data: { kpiEntryId: -1 },
            componentType: 'KpiEntryModal',
          }),
      },
      'separator',
      {
        name: 'New Group',
        action: () => this.props.addKpiGroup(),
      },
      {
        name: 'Remove Group',
        disabled: !params.node,
        action: () => this.props.removeKpiGroup(params.node.data.id),
      },
    ];
  };

  isGroup = (params) =>
    params.node && params.node.data && params.node.data.group;

  getKpiContextMenuItems = (params) => {
    const isGroup = this.isGroup(params);
    const disabled = isGroup || !(params.node && params.node.data);

    return [
      {
        name: 'KPI options',
        disabled: !params.node,
        subMenu: [
          {
            name: 'Move Up',
            shortcut: 'Ctrl + Up',
            action: () => this.props.moveKpiUp(params.node.data.id),
          },
          {
            name: 'Move Down',
            shortcut: 'Ctrl + Down',
            action: () => this.props.moveKpiDown(params.node.data.id),
          },
        ],
      },
      'separator',
      {
        name: 'Edit KPI',
        disabled,
        action: () => this.openKpiEntryModal(params.node.data.id),
      },
      {
        name: 'Remove KPI',
        disabled,
        action: () => this.props.removeKpi(params.node.data.id),
      },
    ];
  };

  getContextMenuItems = (params) => {
    const { colState, setColumnState, setGroupState, path } = this.props;

    const isEmpty = !params.node;
    const isGroup = params.node && params.node.data && params.node.data.group;
    const contextSubMenu =
      isEmpty || isGroup
        ? this.getGroupContextMenuItems(params)
        : this.getKpiContextMenuItems(params);

    const exportDef = {
      columnGroups: true,
      fileName: `KPIs report`,
      processCellCallback: ({ value, column }) => {
        if (isNil(value)) {
          return '';
        }

        switch (value.type) {
          case KpiCalculationResultType.result:
            return value.result;
          case KpiCalculationResultType.error:
            return 'n.a.';
          default:
            return value;
        }
      },
    };

    const baseContextMenu = [
      'separator',
      {
        name: 'Copy',
        disabled: !params.node,
        shortcut: 'Ctrl + C',
        action: () => {
          params.api.copySelectedRangeToClipboard();
        },
      },
      'separator',
      {
        name: 'Detail view',
        checked: this.state.detail,
        action: () =>
          this.setState((prevState) => ({ detail: !prevState.detail })),
      },
      'separator',
      'expandAll',
      'contractAll',
      'separator',
      {
        name: 'Fit All Columns',
        action: () => {
          params.api.sizeColumnsToFit();
          handleGridChange(
            path,
            colState,
            setColumnState,
            setGroupState,
            KPIS_GRID,
            params.columnApi
          );
        },
      },
      'separator',
      {
        name: 'Export',
        subMenu: [
          {
            name: 'CSV Export',
            action: () => {
              params.api.exportDataAsCsv(exportDef);
            },
          },
          {
            name: 'Excel Export',
            action: () => {
              params.api.exportDataAsExcel({ ...exportDef, exportMode: 'xml' });
            },
          },
        ],
      },
    ];

    return contextSubMenu.concat(baseContextMenu);
  };

  getRowNodeId = (data) => `${data.contentType}-${data.id}`;

  render() {
    return (
      <div className="kpis-grid">
        <Grid
          animateRows={true}
          columnDefs={this.getCols(this.state.detail)}
          rowData={this.state.detail ? this.props.rowsDetail : this.props.rows}
          defaultColDef={this.defaultColDef}
          autoGroupColumnDef={this.autoGroupColumnDef}
          frameworkComponents={this.frameworkComponents}
          treeData={true}
          getDataPath={(data) => data.groupPath}
          getRowHeight={this.getRowHeight}
          getRowNodeId={this.getRowNodeId}
          onCellKeyDown={this.onCellKeyDown}
          suppressKeyboardEvent={({ event }) =>
            this.shouldSuppressKeyboardEvent(event)
          }
          enableRangeSelection={true}
          processCellForClipboard={this.processCellForClipboard}
          onCellDoubleClicked={this.onCellDoubleClicked}
          getContextMenuItems={this.getContextMenuItems}
          onGridReady={this.handleGridReady}
          onCellValueChanged={this.handleCellValueChanged}
          onColumnVisible={this.handleColumnVisible}
          onColumnResized={this.handleColumnResized}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  cols: [
    {
      width: 50,
      field: 'equation',
      colId: 'equation',
      cellRenderer: 'equationCellRenderer',
    },
    ...periodColumnsForKpiGridSelector(state, { isGroups: true }),
  ],
  periodIds: periodIdsSelector(state),
  rows: kpiRowsSelector(state),
  rowsDetail: kpiRowsDetailSelector(state),
  colState: gridStateSelector(state),
  settings: settingsSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  addKpiGroup: () => dispatch({ type: ADD_KPI_GROUP }),
  removeKpiGroup: (groupId) => dispatch({ type: REMOVE_KPI_GROUP, groupId }),
  editKpiGroup: (group) => dispatch({ type: EDIT_KPI_GROUP, group }),
  moveKpiGroupUp: (groupId) => dispatch({ type: MOVE_KPI_GROUP_UP, groupId }),
  moveKpiGroupDown: (groupId) =>
    dispatch({ type: MOVE_KPI_GROUP_DOWN, groupId }),
  moveKpiUp: (kpiId) => dispatch({ type: MOVE_KPI_UP, kpiId }),
  moveKpiDown: (kpiId) => dispatch({ type: MOVE_KPI_DOWN, kpiId }),
  removeKpi: (kpiId) => dispatch({ type: KPI_REMOVE, kpiId }),
  openModal: (modalConfig) => dispatch(openModal(modalConfig)),
  setColumnState: (path, columnState) =>
    dispatch({
      type: SET_COLUMN_STATE,
      path,
      grid: KPIS_GRID,
      columnState,
    }),
  setGroupState: (path, groupState) =>
    dispatch({
      type: SET_GROUP_STATE,
      path,
      grid: KPIS_GRID,
      groupState,
    }),
});

KpiGrid.defaultProps = {
  path: KPIS_PATH,
};

export default connect(mapStateToProps, mapDispatchToProps)(KpiGrid);
